import { cn, StatusBadge } from '@ai-tutor/ui';
import { Check } from 'lucide-react';
import { useId, type ReactNode } from 'react';

import type { Plan } from '@/lib/api-client';
import { planDisplayName, type PlanPriceLines } from '@/lib/billing';

/**
 * One card in `PlanPicker`. Not exported from the picker's folder on purpose:
 * a card on its own is how the three pickers drifted apart, so the only way to
 * draw one is through the picker.
 *
 * Two modes, set by whether `radio` is passed:
 *
 * - **Selectable** — the card's body is a `<label>` around a native, visually
 *   hidden `<input type='radio'>`. Native radios sharing a `name` give the
 *   group arrow-key movement, one tab stop and "1 of 2" announcements for
 *   free. The footer sits OUTSIDE the label, so a button in it is never
 *   nested inside the radio's hit area (axe's `nested-interactive`).
 * - **Presentational** — no radio; the recommended card carries the brand
 *   highlight instead, which is how onboarding has always looked.
 *
 * Selection is never colour alone: the chosen card's indicator fills and
 * shows a check, beside the brand border and ring.
 */
export interface PlanOptionProps {
  plan: Plan;
  lines: PlanPriceLines;
  isCurrent: boolean;
  recommended: boolean;
  radio?: { name: string; checked: boolean; onChange: () => void };
  footer?: ReactNode;
  'data-testid'?: string;
}

const CARD =
  'relative flex flex-col rounded-lg border p-5 transition-colors duration-fast';
const HIGHLIGHT =
  'border-brand-600 bg-brand-500/[0.10] shadow-elevation-2 ring-1 ring-brand-600 dark:border-brand-400 dark:bg-brand-400/[0.12] dark:ring-brand-400';

/** `checked` is undefined for a presentational (not selectable) card. */
function cardClassName(highlighted: boolean, checked: boolean | undefined) {
  const selectable = checked !== undefined;
  return cn(
    CARD,
    // `bg-transparent` on the standard card: the surface behind is already a
    // card, so a card-on-card fill is invisible and the highlighted plan had
    // nothing to stand out AGAINST.
    highlighted ? HIGHLIGHT : 'border-border bg-transparent',
    selectable && !checked && 'hover:border-brand-400/60',
    selectable &&
      'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-background'
  );
}

export function PlanOption({
  plan,
  lines,
  isCurrent,
  recommended,
  radio,
  footer,
  'data-testid': testId,
}: PlanOptionProps) {
  const priceId = useId();
  const highlighted = radio ? radio.checked : recommended;
  const body = (
    <PlanOptionBody
      plan={plan}
      lines={lines}
      isCurrent={isCurrent}
      priceId={priceId}
      indicator={radio ? <RadioIndicator checked={radio.checked} /> : null}
    />
  );

  return (
    <div
      data-testid={testId}
      data-plan-id={plan.id}
      data-plan-tier={plan.tier}
      data-selected={radio ? String(radio.checked) : undefined}
      className={cardClassName(highlighted, radio?.checked)}
    >
      {/* `text-brand-950` in dark, not `text-white`: the ramp resolves to a
          light lavender there and white on it fails contrast outright. */}
      {recommended && (
        <span className='absolute -top-2 left-5 rounded-full bg-brand-600 px-2 py-0.5 font-mono text-eyebrow uppercase text-white dark:bg-brand-400 dark:text-brand-950'>
          Popular
        </span>
      )}
      {radio ? (
        <label className='flex flex-1 cursor-pointer flex-col'>
          <input
            type='radio'
            className='sr-only'
            name={radio.name}
            value={plan.id}
            checked={radio.checked}
            onChange={radio.onChange}
            aria-label={`${planDisplayName(plan)}${
              isCurrent ? ', current plan' : ''
            }`}
            aria-describedby={priceId}
          />
          {body}
        </label>
      ) : (
        <div className='flex flex-1 flex-col'>{body}</div>
      )}
      {footer && <div className='mt-auto'>{footer}</div>}
    </div>
  );
}

function PlanOptionBody({
  plan,
  lines,
  isCurrent,
  priceId,
  indicator,
}: {
  plan: Plan;
  lines: PlanPriceLines;
  isCurrent: boolean;
  priceId: string;
  indicator: ReactNode;
}) {
  return (
    <>
      <div className='flex items-start justify-between gap-2'>
        <div className='flex items-center gap-2'>
          {indicator}
          <h3 className='text-body font-semibold text-foreground'>
            {plan.name}
          </h3>
        </div>
        {isCurrent && <StatusBadge variant='neutral'>Current plan</StatusBadge>}
      </div>
      <div id={priceId}>
        <div className='mb-0.5 mt-1 flex items-baseline gap-2'>
          {lines.strikePrice && (
            <span className='text-body text-muted-foreground line-through'>
              {lines.strikePrice}
            </span>
          )}
          <span className='text-title text-foreground'>
            {lines.price}
            <span className='ml-1 text-label font-normal text-muted-foreground'>
              /{lines.unit}
            </span>
          </span>
        </div>
        {/* Reserved heights so monthly and yearly keep the SAME card height
            (yearly fills these two lines - the saving, then the billing facts;
            monthly leaves them empty). `min-h` rather than `h`: in a narrow
            dialog the second line wraps. */}
        <div className='min-h-5 text-label font-medium text-success-emphasis'>
          {lines.savings}
        </div>
        <div className='mb-4 mt-0.5 min-h-4 text-label text-muted-foreground'>
          {lines.sub}
        </div>
      </div>
      <FeatureList features={plan.features} />
    </>
  );
}

function RadioIndicator({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden='true'
      className={cn(
        'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
        checked
          ? 'border-brand-600 bg-brand-600 text-white dark:border-brand-400 dark:bg-brand-400 dark:text-brand-950'
          : 'border-muted-foreground/60 bg-background'
      )}
    >
      {checked && <Check className='h-3 w-3' strokeWidth={3} />}
    </span>
  );
}

function FeatureList({ features }: { features: string[] }) {
  if (features.length === 0) return null;
  return (
    <ul className='mb-5 space-y-2'>
      {features.map(f => (
        <li
          key={f}
          className='flex items-start gap-2 text-body text-muted-foreground'
        >
          <Check
            aria-hidden='true'
            className='mt-0.5 h-4 w-4 flex-shrink-0 text-brand-600 dark:text-brand-400'
          />
          <span>{f}</span>
        </li>
      ))}
    </ul>
  );
}
